import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import moment from 'moment';

import {
	getLocalTime,
	getUTCTimeFromLocal,
} from '../../../common/utils/time/time';

const GET_RETURN_SERVICE_RUNS = gql`
	query getReturnServiceRuns(
		$page: Int,
		$perPage: Int,
		$sortBy: String,
		$sortDirection: Int,
		$filter: ServiceRunPageFilter
	) {
		getServiceRuns(
			page: $page,
			perPage: $perPage,
			sortBy: $sortBy,
			sortDirection: $sortDirection,
			filter: $filter
		) {
			items {
				id
				planned
				route {
					id
					name
				}
				driver {
					id
					firstname
					lastname
					username
				}
				warehouse {
					id
					name
				}
				returnWarehouse {
					id
					name
				}
			}
			count
		}
	}
`;

const getDriverName = (driver) => {
	if (!driver) {
		return 'No driver';
	}

	return [driver.firstname, driver.lastname].filter(Boolean).join(' ') ||
		driver.username ||
		'Unnamed driver';
};

const getServiceRunLabel = (serviceRun) => {
	const routeName = serviceRun?.route?.name || 'Unnamed route';
	const driverName = getDriverName(serviceRun?.driver);
	return `${routeName} — ${driverName}`;
};

// Service runs planned on the selected state-local run date, as picker options.
const useReturnServiceRuns = ({ isOpen, runDate, timezone }) => {
	const plannedDateRange = useMemo(() => {
		const parsedRunDate = moment(runDate, 'YYYY-MM-DD', true);
		if (!parsedRunDate.isValid()) {
			return null;
		}

		const toUtcUnixAtLocalNoon = localDate => getUTCTimeFromLocal(
			timezone,
			localDate.hours(12).minutes(0).seconds(0).milliseconds(0),
		).unix();

		// Keep a buffered query for older server releases, then enforce the
		// selected state-local day below.
		return {
			from: toUtcUnixAtLocalNoon(parsedRunDate.clone().subtract(1, 'day')),
			to: toUtcUnixAtLocalNoon(parsedRunDate.clone().add(1, 'day')),
		};
	}, [runDate, timezone]);

	const {
		data: serviceRunData,
		loading: serviceRunsLoading,
		error: serviceRunsError,
	} = useQuery(GET_RETURN_SERVICE_RUNS, {
		variables: {
			page: 0,
			perPage: 300,
			sortBy: 'order',
			sortDirection: 1,
			filter: {
				plannedFromDate: plannedDateRange?.from,
				plannedToDate: plannedDateRange?.to,
			},
		},
		fetchPolicy: 'network-only',
		skip: !isOpen || !plannedDateRange,
	});

	const serviceRuns = useMemo(() => (
		(serviceRunData?.getServiceRuns?.items || []).filter(serviceRun => (
			serviceRun?.planned &&
			getLocalTime(timezone, serviceRun.planned).format('YYYY-MM-DD') === runDate
		))
	), [runDate, serviceRunData, timezone]);
	const serviceRunOptions = useMemo(() => (
		serviceRuns.map(serviceRun => ({
			label: getServiceRunLabel(serviceRun),
			value: serviceRun.id,
		}))
	), [serviceRuns]);

	return {
		plannedDateRange,
		serviceRuns,
		serviceRunOptions,
		serviceRunsLoading,
		serviceRunsError,
	};
};

export default useReturnServiceRuns;
